import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef
} from '@angular/core';
import { AsButtonDirective } from '@directives/as-button/as-button.directive';
import { StopClickPropagationDirective } from '@directives/stop-click-propagation/stop-click-propagation.directive';
import { LineAsset, UserAsset, UserAssetData } from '@models/dashboard.model';
import {
  MULTI_SIM_TYPES,
  calculateMultiSimType,
  constructAssetTitle,
  hasMultiSim
} from '@models/functions.model';
import { NgbAlertModule, NgbOffcanvas, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { DashboardService } from '@services/dashboard/dashboard.service';
import { MultisimOffcanvasComponent } from '@shared-components/multisim-offcanvas/multisim-offcanvas.component';
import { Subscription } from 'rxjs';
import { DataLoaderDirective } from 'tangoe-design-system';

import { AssetSelectionItemComponent } from '../selection-item/asset-selection-item.component';

@Component({
  selector: 'ts-asset-selection',
  templateUrl: './asset-selection.component.html',
  imports: [
    CommonModule,
    NgbAlertModule,
    NgbTooltipModule,
    TranslationPipe,
    AsButtonDirective,
    DataLoaderDirective,
    StopClickPropagationDirective,
    AssetSelectionItemComponent,
    MultisimOffcanvasComponent
  ]
})
export class AssetSelectionComponent implements OnInit, OnDestroy {
  @Input() header: string;
  @Input() no_asset_text: string;
  @Input() no_asset_alert: string;
  @Input() asset_fields: string[] = [
    'imei',
    'imei2',
    'serial_number',
    'sim_compatibility',
    'plan',
    'sim_number',
    'add_on_features',
    'contract_end_date',
    'upgrade_eligible_date'
  ];
  @Input() existing_asset: UserAsset | undefined;
  @Output() assets_response_emit: EventEmitter<UserAssetData> = new EventEmitter();
  @Output() asset_selection_emit: EventEmitter<UserAsset> = new EventEmitter();
  @ContentChild(TemplateRef) cardTemplate: TemplateRef<any>;
  user_assets: UserAsset[] | undefined = undefined;
  offcanvas_asset: UserAsset;
  multi_sim_types: string[] = MULTI_SIM_TYPES;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_state_svc: ActivityStateService,
    private dashboard_svc: DashboardService,
    private offcanvas_svc: NgbOffcanvas
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.dashboard_svc
        .userAssets({
          orderTypeId: this.activity_state_svc.orderTypeId()!,
          countryId: this.activity_state_svc.parameter('country')?.id
        })
        .subscribe((assets_response: UserAssetData) => {
          this.user_assets = assets_response.assets;
          this.user_assets.forEach((asset: UserAsset) => {
            asset.multi_sim_type = calculateMultiSimType(asset);
          });

          this.activity_state_svc.setSteps(assets_response.progress_bar_config.steps);

          this.pinCartDevice();

          this.assets_response_emit.emit(assets_response);
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  assetAriaLabel(asset: UserAsset): string {
    return constructAssetTitle(asset);
  }

  selectAsset(asset: UserAsset): void {
    (document.activeElement as HTMLElement).blur();
    this.asset_selection_emit.emit(asset);
  }

  isSelectedAsset(asset: UserAsset): boolean | undefined {
    if (!this.existing_asset) return undefined;

    switch (asset.multi_sim_type) {
      case '1DML': {
        let lineMatch = asset?.line?.id === this.existing_asset?.line?.id;

        if (asset.additional_lines) {
          asset.additional_lines.forEach((line: LineAsset) => {
            if (line.id === this.existing_asset?.line?.id) lineMatch = true;
          });
        }

        const deviceMatch =
          !!asset.device?.id && asset.device.id === this.existing_asset?.device?.id;

        const overrideMatch =
          this.existing_asset.device?.is_override === true &&
          asset.device?.id === this.existing_asset.device?.existing_asset_id;

        return (
          (deviceMatch &&
            lineMatch &&
            asset.multi_sim_type === this.existing_asset.multi_sim_type) ||
          (overrideMatch && asset.multi_sim_type === this.existing_asset.multi_sim_type)
        );
      }

      case '1LMD':
      case '1LMD_with_ref_device': {
        const lineMatch = asset.line?.id === this.existing_asset.line?.id;
        const multiSimMatch = asset.multi_sim_type === this.existing_asset.multi_sim_type;
        /*
         * For 1LMD_with_ref_device:
         *
         * existing_asset.device is a REFERENCE device.
         * It does not contain the actual selected device ID.
         *
         * Therefore we must use the line as the persisted
         * identity of the selected asset.
         */
        return lineMatch && multiSimMatch;
      }

      case 'device_with_line': {
        const deviceMatch =
          !!asset.device?.id && asset.device.id === this.existing_asset?.device?.id;

        const catalogMatch =
          !!asset.device?.device_catalog_id &&
          asset.device.device_catalog_id === this.existing_asset?.device?.device_catalog_id;

        const lineMatch = !!asset.line?.id && asset.line.id === this.existing_asset?.line?.id;

        const overrideMatch =
          this.existing_asset.device?.is_override === true &&
          asset.device?.id === this.existing_asset.device?.existing_asset_id;

        return (lineMatch && (deviceMatch || catalogMatch)) || overrideMatch;
      }

      case 'line_with_ref_device': {
        const deviceMatch =
          !!asset.device?.id && asset.device.id === this.existing_asset?.device?.id;

        const catalogMatch =
          !!asset.device?.device_catalog_id &&
          asset.device.device_catalog_id === this.existing_asset?.device?.device_catalog_id;

        const lineMatch = !!asset.line?.id && asset.line.id === this.existing_asset?.line?.id;

        const overrideMatch =
          this.existing_asset.device?.is_override === true &&
          asset.device?.id === this.existing_asset.device?.existing_asset_id;

        return (lineMatch && (deviceMatch || catalogMatch)) || overrideMatch;
      }

      case 'device':
      case 'line':
      default: {
        const deviceMatch =
          !!asset.device?.id && asset.device.id === this.existing_asset?.device?.id;

        const catalogMatch =
          !!asset.device?.device_catalog_id &&
          asset.device.device_catalog_id === this.existing_asset?.device?.device_catalog_id;

        const lineMatch = !!asset.line?.id && asset.line.id === this.existing_asset?.line?.id;

        const overrideMatch =
          this.existing_asset.device?.is_override === true &&
          asset.device?.id === this.existing_asset.device?.existing_asset_id;

        return (
          (asset.multi_sim_type === this.existing_asset.multi_sim_type &&
            (deviceMatch || catalogMatch || lineMatch)) ||
          overrideMatch
        );
      }
    }
  }

  // TODO move below public function to shared?
  hasMultiSim(asset: UserAsset): boolean {
    return hasMultiSim(asset);
  }

  openAssetOffCanvas(content: TemplateRef<any>, asset: UserAsset): void {
    this.offcanvas_asset = asset;

    this.offcanvas_svc.open(content, {
      position: 'end',
      panelClass: 'multisim-offcanvas',
      backdrop: 'static'
    });
  }

  private pinCartDevice(): void {
    if (
      !this.existing_asset ||
      !this.user_assets ||
      this.existing_asset.asset_selection_case !== 1
    ) return;

    const selectedIndex = this.user_assets.findIndex(
      (asset: UserAsset) => this.isSelectedAsset(asset) === true
    );
    if (selectedIndex === -1) return;

    const selectedAsset = this.user_assets[selectedIndex];
    if (selectedAsset.device) selectedAsset.device.in_cart = true;
    if (selectedAsset.line) selectedAsset.line.in_cart = true;

    this.user_assets.splice(selectedIndex, 1);
    this.user_assets.unshift(selectedAsset);
  }
}
